(function () {
  'use strict';

  const WIDGET_SELECTOR = '[data-shipping-protection]';

  /**
   * Find shipping protection widget(s) in the DOM
   */
  function findWidgets(root = document) {
    return Array.from(root.querySelectorAll(WIDGET_SELECTOR));
  }

  /**
   * Get widget configuration from data attributes
   */
  function getWidgetConfig(widget) {
    if (!widget) return null;

    const variantId = widget.dataset.variantId;
    const lineKey = widget.dataset.lineKey || '';
    const iconEnabled = widget.dataset.iconEnabled || '';
    const iconDisabled = widget.dataset.iconDisabled || '';
    const descEnabled = widget.dataset.descEnabled || '';
    const descDisabled = widget.dataset.descDisabled || '';

    if (!variantId) return null;

    return {
      variantId: variantId,
      lineKey: lineKey,
      iconEnabled: iconEnabled,
      iconDisabled: iconDisabled,
      descEnabled: descEnabled,
      descDisabled: descDisabled,
    };
  }

  /**
   * Fetch JSON from URL
   */
  async function fetchJSON(url, options = {}) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[ShippingProtection] Request failed:', error);
      throw error;
    }
  }

  /**
   * Get current cart state
   */
  async function getCart() {
    try {
      return await fetchJSON('/cart.js');
    } catch (error) {
      console.error('[ShippingProtection] Failed to fetch cart:', error);
      return null;
    }
  }

  /**
   * Get sections to render (includes both mini-cart and main cart sections)
   */
  function getSectionsToRender() {
    const miniCart = document.querySelector('mini-cart');
    const cartItems = document.querySelector('cart-items');
    
    // If on cart page, use cart-items sections (includes main cart sections)
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return cartItems.getSectionsToRender().map((section) => section.section);
    }
    
    // Otherwise use mini-cart sections
    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      return miniCart.getSectionsToRender().map((section) => section.id);
    }
    
    return [];
  }

  /**
   * Update cart page sections (main-cart-items and main-cart-footer)
   */
  function updateCartPageSections(responseData) {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || !responseData.sections) return;

    if (typeof cartItems.getSectionsToRender === 'function' && typeof cartItems.getSectionInnerHTML === 'function') {
      cartItems.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const elementToReplace = element.querySelector(section.selector) || element;
          if (elementToReplace && responseData.sections[section.section]) {
            elementToReplace.innerHTML = cartItems.getSectionInnerHTML(
              responseData.sections[section.section],
              section.selector
            );
          }
        }
      });
    }
  }

  /**
   * Add variant to cart with sections support
   */
  async function addVariantToCart(variantId) {
    try {
      const miniCart = document.querySelector('mini-cart');
      const sections = getSectionsToRender();

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname,
        properties: {
          '_shipping_protection': 'true'
        }
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add variant');
      }

      const responseData = await response.json();

      // Update cart page sections if on cart page
      updateCartPageSections(responseData);

      // Update mini cart if available (exact same approach as buy-x-get-y)
      if (miniCart && miniCart.renderContents && responseData.sections) {
        // Verify all required sections exist before rendering
        const sectionsToRender = miniCart.getSectionsToRender();
        const allSectionsExist = sectionsToRender.every(
          (section) => responseData.sections[section.id]
        );
        
        if (allSectionsExist) {
          miniCart.renderContents(responseData);
        } else {
          console.warn('[ShippingProtection] Some sections missing from response', {
            requested: sectionsToRender.map(s => s.id),
            received: Object.keys(responseData.sections || {})
          });
        }
      }

      // Re-initialize widgets after DOM updates
      setTimeout(() => {
        initAllWidgets();
      }, 50);

      // Dispatch cart updated event
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart: responseData,
          },
        })
      );

      return true;
    } catch (error) {
      console.error('[ShippingProtection] Failed to add variant:', error);
      return false;
    }
  }

  /**
   * Remove line item from cart using update.js (same approach as buy-x-get-y)
   */
  async function removeLineFromCart(lineKey) {
    try {
      const cartItems = document.querySelector('cart-items');
      const sections = cartItems
        ? cartItems.getSectionsToRender().map((section) => section.section)
        : getSectionsToRender();

      const body = JSON.stringify({
        updates: {
          [lineKey]: 0,
        },
        sections: sections,
        sections_url: window.location.pathname,
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove line');
      }

      const responseData = await response.json();

      // Update cart page sections if on cart page
      updateCartPageSections(responseData);

      // Update mini cart if available (exact same approach as buy-x-get-y)
      const miniCart = document.querySelector('mini-cart');
      if (miniCart && miniCart.renderContents && responseData.sections) {
        // Verify all required sections exist before rendering
        const sectionsToRender = miniCart.getSectionsToRender();
        const allSectionsExist = sectionsToRender.every(
          (section) => responseData.sections[section.id]
        );
        
        if (allSectionsExist) {
          miniCart.renderContents(responseData);
        } else {
          console.warn('[ShippingProtection] Some sections missing from response', {
            requested: sectionsToRender.map(s => s.id),
            received: Object.keys(responseData.sections || {})
          });
        }
      }

      // Re-initialize widgets after DOM updates
      setTimeout(() => {
        initAllWidgets();
      }, 50);

      // Dispatch cart updated event
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart: responseData,
          },
        })
      );

      return true;
    } catch (error) {
      console.error('[ShippingProtection] Failed to remove line:', error);
      return false;
    }
  }

  /**
   * Check if variant is in cart
   */
  function isVariantInCart(cart, variantId) {
    if (!cart || !cart.items || !variantId) return false;
    return cart.items.some(
      (item) => String(item.variant_id) === String(variantId)
    );
  }

  /**
   * Get line item from cart
   */
  function getLineItem(cart, variantId) {
    if (!cart || !cart.items || !variantId) return null;
    return (
      cart.items.find((item) => String(item.variant_id) === String(variantId)) ||
      null
    );
  }


  /**
   * Check if cart is empty (excluding protection items) and remove protection if needed
   */
  async function checkAndRemoveProtectionIfCartEmpty() {
    const widgets = findWidgets();
    if (widgets.length === 0) return;

    const widget = widgets[0];
    const config = getWidgetConfig(widget);
    if (!config || !config.variantId) return;

    const cart = await getCart();
    if (!cart || !cart.items) return;

    // Count non-protection items
    const nonProtectionItems = cart.items.filter((item) => {
      return String(item.variant_id) !== String(config.variantId);
    });

    // If cart is empty (only protection items or completely empty), remove protection
    if (nonProtectionItems.length === 0 && cart.items.length > 0) {
      const protectionItem = cart.items.find(
        (item) => String(item.variant_id) === String(config.variantId)
      );

      if (protectionItem && protectionItem.key) {
        // Remove protection product
        await removeLineFromCart(protectionItem.key);
      }
    }
  }

  /**
   * Update widget data-line-key attribute (only thing needed since widget is re-rendered by Liquid)
   */
  async function updateWidgetLineKey(widget) {
    const config = getWidgetConfig(widget);
    if (!config) return;

    const cart = await getCart();
    if (!cart) return;

    const lineItem = getLineItem(cart, config.variantId);
    if (lineItem && lineItem.key) {
      widget.dataset.lineKey = lineItem.key;
    }
  }

  /**
   * Handle checkbox toggle
   */
  async function handleToggle(widget, checkbox) {
    const config = getWidgetConfig(widget);
    if (!config) return;

    // Get loading overlay element
    const loadingOverlay = widget.querySelector('.sp-widget__loading');
    
    // Disable checkbox and show loader during operation
    checkbox.disabled = true;
    widget.classList.add('loading');
    if (loadingOverlay) {
      loadingOverlay.classList.remove('hidden');
    }
    const wasChecked = checkbox.checked;

    try {
      const cart = await getCart();
      if (!cart) {
        // Revert checkbox state on error
        checkbox.checked = !wasChecked;
        checkbox.disabled = false;
        widget.classList.remove('loading');
        if (loadingOverlay) {
          loadingOverlay.classList.add('hidden');
        }
        return;
      }

      const isCurrentlyInCart = isVariantInCart(cart, config.variantId);
      const lineItem = getLineItem(cart, config.variantId);

      let success = false;

      if (wasChecked && !isCurrentlyInCart) {
        // Add to cart
        success = await addVariantToCart(config.variantId);
      } else if (!wasChecked && isCurrentlyInCart && lineItem) {
        // Remove from cart
        success = await removeLineFromCart(lineItem.key || config.lineKey);
      } else {
        // Already in desired state
        success = true;
      }

      if (success) {
        // Widget is already re-rendered by Liquid via renderContents() in addVariantToCart/removeLineFromCart
        // Only update line-key attribute if needed (for future operations)
        await updateWidgetLineKey(widget);
      } else {
        // Revert checkbox state on failure
        checkbox.checked = !wasChecked;
      }
    } catch (error) {
      console.error('[ShippingProtection] Toggle failed:', error);
      // Revert checkbox state on error
      checkbox.checked = !wasChecked;
    } finally {
      checkbox.disabled = false;
      widget.classList.remove('loading');
      // Hide loading overlay
      if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
      }
    }
  }

  /**
   * Initialize widget event listeners
   */
  function initWidget(widget) {
    const checkbox = widget.querySelector('.sp-switch input[type="checkbox"]');
    if (!checkbox) return;

    // Remove existing listeners (if any) by cloning the element
    const newCheckbox = checkbox.cloneNode(true);
    checkbox.parentNode.replaceChild(newCheckbox, checkbox);

    // Add event listener
    newCheckbox.addEventListener('change', () => {
      handleToggle(widget, newCheckbox);
    });
  }

  /**
   * Initialize all widgets on the page
   */
  function initAllWidgets() {
    const widgets = findWidgets();
    widgets.forEach((widget) => {
      initWidget(widget);
      // Widget state is already correct from Liquid rendering
      // Only update line-key if needed
      updateWidgetLineKey(widget);
    });
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllWidgets);
  } else {
    initAllWidgets();
  }

  /**
   * Watch for dynamically added widgets (e.g., in cart drawer)
   */
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check if the added node is a widget
          if (node.matches && node.matches(WIDGET_SELECTOR)) {
            initWidget(node);
            updateWidgetLineKey(node);
          }
          // Check if widgets were added inside this node
          const widgets = findWidgets(node);
          widgets.forEach((widget) => {
            initWidget(widget);
            updateWidgetLineKey(widget);
          });
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  /**
   * Listen for cart updates to check if protection should be removed
   */
  document.addEventListener('cart:updated', () => {
    setTimeout(() => {
      checkAndRemoveProtectionIfCartEmpty();
    }, 200);
  });

  /**
   * Expose refresh function for external use
   */
  window.ShippingProtectionWidget = {
    refresh: function () {
      initAllWidgets();
    },
  };
})();
